import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Bot, Send, Loader, CheckCircle, AlertCircle, Clock,
  User, Zap, TrendingUp, Users, Calendar, DollarSign,
  FileText, Settings, MessageSquare, ArrowUp
} from 'lucide-react';
import './ManagerAgent.css';

// Lightweight markdown → HTML for agent messages
const formatMessage = (text) => {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')   // escape HTML
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')                      // **bold**
    .replace(/\*(.+?)\*/g, '<em>$1</em>')                                  // *italic*
    .replace(/^[-•]\s+(.*)$/gm, '<li>$1</li>')                             // bullet lines
    .replace(/((?:<li>.*<\/li>\s*)+)/g, '<ul>$1</ul>')                     // wrap consecutive <li> in <ul>
    .replace(/\n/g, '<br/>');                                               // newlines
};

const ManagerAgent = ({ onClose, hotelId }) => {
  const { currentUser, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Initial greeting and capabilities
    const welcomeMessage = {
      id: Date.now(),
      role: 'agent',
      content: `Hello ${userProfile?.fullName || 'Manager'}! 👋\nI'm **NOVA**, your AI Manager Assistant.\n\nHere's what I can do for you:\n- 📊 **Analytics** – Occupancy, revenue & performance insights\n- 📅 **Bookings** – Reservations, check-ins & check-outs\n- 👥 **Staff** – Schedules, tasks & coordination\n- 🔔 **Issues** – Maintenance, complaints & alerts\n- 📈 **Reports** – Forecasting & trend analysis\n- 🤖 **Automation** – Routine hotel operations\n\nHow can I help you today?`,
      timestamp: new Date()
    };

    setMessages([welcomeMessage]);

    // Set suggested actions
    setSuggestedActions([
      { icon: TrendingUp, label: 'Show today\'s performance', action: 'performance' },
      { icon: Users, label: 'Check staff status', action: 'staff' },
      { icon: Calendar, label: 'View upcoming bookings', action: 'bookings' },
      { icon: AlertCircle, label: 'Any urgent issues?', action: 'issues' }
    ]);
  }, [userProfile]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Build ref so handleSend can always read latest sessionId
  const sessionIdRef = useRef(null);
  const historyRef   = useRef([]);

  // Core send logic — shared by handleSend and quick actions
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const { managerChat, sendChat, createSession } = await import('../services/aiService');
      let token = null;
      try { token = await currentUser?.getIdToken?.(); } catch (_) {}

      const ctx = { role: 'manager', ...(hotelId ? { hotel_id: String(hotelId) } : {}) };

      let res;
      if (sessionIdRef.current) {
        try {
          res = await sendChat(sessionIdRef.current, text, token);
        } catch (chatErr) {
          // Session expired (e.g. server reloaded) — recreate and retry once
          if (chatErr.status === 404 || chatErr.status === 422) {
            try {
              const sess = await createSession('manager', ctx, token);
              sessionIdRef.current = sess.session_id;
              res = await sendChat(sessionIdRef.current, text, token);
            } catch (_retryErr) {
              throw _retryErr;
            }
          } else {
            throw chatErr;
          }
        }
      } else {
        res = await managerChat(text, historyRef.current, ctx, token);
      }

      // Keep history for stateless fallback
      historyRef.current = [
        ...historyRef.current,
        { role: 'user',      content: text },
        { role: 'assistant', content: res.reply },
      ].slice(-20);

      const toolActions = (res.actions || []).map(a => ({
        label: `${a.tool}`,
        type:  a.tool,
        result: a.result,
      }));

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'agent',
        content: res.reply,
        actions: toolActions,
        needsEscalation: !!(res.escalation),
        timestamp: new Date()
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'agent',
        content: 'Sorry, I could not reach the AI server. Please ensure the AI API is running on port 8001.',
        timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTyping, hotelId, currentUser, userProfile]);

  // Create manager agent session on mount
  useEffect(() => {
    let cancelled = false;
    async function initSession() {
      try {
        const { createSession } = await import('../services/aiService');
        let token = null;
        try { token = await currentUser?.getIdToken?.(); } catch (_) {}
        const ctx = { role: 'manager' };
        if (hotelId) ctx.hotel_id = String(hotelId);
        const sess = await createSession('manager', ctx, token);
        if (!cancelled) sessionIdRef.current = sess.session_id;
      } catch (_) {
        // session creation failed — will fall back to direct calls
      }
    }
    initSession();
    return () => { cancelled = true; };
  }, [hotelId, currentUser, userProfile]);

  const handleSend = async () => {
    sendMessage(inputValue);
  };

  // eslint-disable-next-line no-unused-vars
  const _generateAgentResponse_DEPRECATED = (query) => {
    const lowercaseQuery = query.toLowerCase();

    // Performance/Analytics queries
    if (lowercaseQuery.includes('performance') || lowercaseQuery.includes('analytics') || lowercaseQuery.includes('revenue')) {
      return {
        content: `📊 **Today's Performance Summary:**

• **Occupancy Rate:** 70% (35/50 rooms occupied)
• **Revenue Today:** $4,500
• **Check-ins Today:** 8 guests
• **Check-outs Today:** 5 guests
• **Average Rating:** 4.5/5

**Trend Analysis:** 
Revenue is up 12% compared to last week. Occupancy is strong for this time of year. Weekend bookings are at 85% capacity.

Would you like me to generate a detailed report or forecast for the next month?`,
        actions: [
          { label: 'Generate Full Report', type: 'report' },
          { label: 'View Forecast', type: 'forecast' }
        ]
      };
    }

    // Staff management queries
    if (lowercaseQuery.includes('staff') || lowercaseQuery.includes('employee') || lowercaseQuery.includes('team')) {
      return {
        content: `👥 **Staff Status Update:**

• **On Duty:** 24 staff members
• **Scheduled Today:** 28 staff members
• **Pending Shifts:** 2 shift swaps need approval
• **Issues:** 1 call-out reported (Room Service - covered by standby)

**Department Status:**
✅ Front Desk - Fully staffed
✅ Housekeeping - Fully staffed
⚠️ Maintenance - 1 position open
✅ Food & Beverage - Fully staffed

All critical positions are covered. Would you like me to approve the pending shift swaps or post the maintenance position?`,
        actions: [
          { label: 'Approve Shift Swaps', type: 'approve' },
          { label: 'Post Job Opening', type: 'job' }
        ]
      };
    }

    // Booking queries
    if (lowercaseQuery.includes('booking') || lowercaseQuery.includes('reservation') || lowercaseQuery.includes('guest')) {
      return {
        content: `📅 **Booking Overview:**

**Today:**
• 8 Check-ins scheduled (3 completed, 5 pending)
• 5 Check-outs scheduled (all completed)
• 12 Pending confirmations

**Upcoming (Next 7 Days):**
• 45 Arrivals
• 38 Departures
• Occupancy forecast: 78% average

**VIP Guests:** 2 VIP arrivals tomorrow - rooms prepared and welcome packages arranged.

All bookings are being monitored. I'll alert you if any special requests or issues arise.`,
        actions: [
          { label: 'View Full Calendar', type: 'calendar' },
          { label: 'Send Guest Messages', type: 'message' }
        ]
      };
    }

    // Issues/Problems queries
    if (lowercaseQuery.includes('issue') || lowercaseQuery.includes('problem') || lowercaseQuery.includes('urgent')) {
      return {
        content: `🔔 **Current Issues & Status:**

**Urgent (Needs Attention):**
🔴 Room 310 - AC not cooling properly
   - Maintenance dispatched 15 mins ago
   - Guest offered room change (declined)
   - Temporary portable AC provided
   - **Escalation:** Requires follow-up in 1 hour

**Medium Priority (Being Handled):**
🟡 Low inventory alert - Pool towels below threshold
   - Reorder placed automatically
   - ETA: Tomorrow morning
   - Temporary solution: Using backup stock

**Resolved Today:**
✅ 3 maintenance requests completed
✅ 2 guest complaints resolved
✅ 1 billing discrepancy corrected

I'll continue monitoring Room 310 and escalate if not resolved within the hour. Do you want to personally follow up now?`,
        actions: [
          { label: 'Follow Up on Room 310', type: 'followup' },
          { label: 'View All Issues', type: 'issues' }
        ],
        needsEscalation: true
      };
    }

    // Reports queries
    if (lowercaseQuery.includes('report') || lowercaseQuery.includes('document') || lowercaseQuery.includes('summary')) {
      return {
        content: `📄 **Report Options:**

I can generate the following reports for you:

• **Daily Operations Report** - Today's summary
• **Weekly Performance Report** - Last 7 days
• **Monthly Financial Report** - Revenue & expenses
• **Occupancy Analysis** - Trends and forecasts
• **Guest Satisfaction Report** - Reviews and ratings
• **Staff Performance Report** - Team metrics

Which report would you like me to generate? I can email it to you or download it directly.`,
        actions: [
          { label: 'Daily Report', type: 'report-daily' },
          { label: 'Weekly Report', type: 'report-weekly' },
          { label: 'Monthly Report', type: 'report-monthly' }
        ]
      };
    }

    // Inventory queries
    if (lowercaseQuery.includes('inventory') || lowercaseQuery.includes('stock') || lowercaseQuery.includes('supplies')) {
      return {
        content: `📦 **Inventory Status:**

**Low Stock Alerts:**
⚠️ Pool towels - 15 remaining (reorder placed)
⚠️ Mini bar snacks - 3 days supply
⚠️ Cleaning supplies - 5 days supply

**Adequate Stock:**
✅ Bed linens - 2 weeks supply
✅ Toiletries - 3 weeks supply
✅ Office supplies - 1 month supply

**Pending Orders:**
• Pool towels - Arriving tomorrow
• Mini bar restock - Arriving in 2 days
• Cleaning supplies - Scheduled for next week

I've automatically placed orders for critical items. Would you like to adjust any reorder thresholds?`,
        actions: [
          { label: 'View Full Inventory', type: 'inventory' },
          { label: 'Adjust Thresholds', type: 'settings' }
        ]
      };
    }

    // Default/General response
    return {
      content: `I understand you're asking about "${query}". 

Here's what I can help you with:

• Ask about **performance** and **analytics**
• Check **staff** status and scheduling
• Review **bookings** and reservations
• Monitor **issues** and escalations
• Generate **reports** and summaries
• Manage **inventory** and supplies

Try asking something like:
- "Show me today's performance"
- "Any urgent issues?"
- "Check staff status"
- "Generate a report"

I'm here to help you manage your hotel efficiently!`,
      actions: []
    };
  };

  const handleSuggestedAction = (action) => {
    const actionMessages = {
      performance: "Show me today's performance",
      staff: 'Check staff status',
      bookings: 'Show upcoming bookings',
      issues: 'Are there any urgent issues?'
    };
    const msg = actionMessages[action] || '';
    if (!msg) return;
    // Call sendMessage directly instead of going through state
    sendMessage(msg);
  };

  const handleActionClick = (actionType) => {
    // Re-ask the agent about this specific action type
    const actionQueries = {
      report: 'Generate a daily operations report',
      forecast: 'Show me the occupancy forecast for the next 7 days',
      approve: 'Show me the pending shift swap requests',
      job: 'What open positions do we need to fill?',
      calendar: 'Show me the full booking calendar',
      message: 'Help me send a message to all guests',
      followup: 'What is the current status of maintenance issues?',
      issues: 'List all current issues and their status',
      inventory: 'Show me the full inventory status',
      settings: 'What are the current reorder thresholds?',
      'report-daily': 'Generate a detailed daily operations report',
      'report-weekly': 'Generate a weekly performance report',
      'report-monthly': 'Generate a monthly financial report',
    };
    const query = actionQueries[actionType] || `Tell me more about ${actionType}`;
    sendMessage(query);
  };

  return (
    <div className="manager-agent">
      <div className="agent-header">
        <div className="agent-header-content">
          <Bot size={28} className="agent-icon" />
          <div>
            <h2>Manager AI Assistant</h2>
            <p className="agent-status">
              <span className="status-dot"></span>
              Online & Monitoring
            </p>
          </div>
        </div>
        <button className="agent-close" onClick={onClose}>×</button>
      </div>

      <div className="agent-messages">
        {messages.map(message => (
          <div key={message.id} className={`message message-${message.role}`}>
            <div className="message-avatar">
              {message.role === 'agent' ? <Bot size={20} /> : <User size={20} />}
            </div>
            <div className="message-content">
              <div className="message-text" dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }} />
              {message.timestamp && (
                <div className="message-timestamp">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              {message.needsEscalation && (
                <div className="escalation-badge">
                  <ArrowUp size={14} />
                  <span>Needs Your Attention</span>
                </div>
              )}
              {message.actions && message.actions.length > 0 && (
                <div className="message-actions">
                  {message.actions.map((action, index) => (
                    <button
                      key={index}
                      className="action-btn"
                      onClick={() => handleActionClick(action.type)}
                    >
                      <Zap size={14} />
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="message message-agent">
            <div className="message-avatar">
              <Bot size={20} />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {suggestedActions.length > 0 && messages.length === 1 && (
        <div className="suggested-actions">
          <p>Quick actions:</p>
          <div className="suggested-actions-grid">
            {suggestedActions.map((action, index) => (
              <button
                key={index}
                className="suggested-action-btn"
                onClick={() => handleSuggestedAction(action.action)}
              >
                <action.icon size={16} />
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="agent-input">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Ask me anything about your hotel operations..."
          disabled={isTyping}
        />
        <button
          className="send-btn"
          onClick={handleSend}
          disabled={!inputValue.trim() || isTyping}
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
};

export default ManagerAgent;
